import { useState } from 'react'

import { Button, IconButton } from '../Button/index.js'
import { ColorCircle, ColorPicker } from '../ColorPicker/index.js'
import { useDocument } from '../hooks/useDocument.js'
import { SvgIcon } from '../Icon/SvgIcon.js'
import { Input } from '../Input/index.js'
import { ColoredLabel } from './types.js'
import { colorStylesFactory } from './utils.js'

export const LabelPickerItem = ({
  item,
  active,
  onEdit,
  onRemove,
  onCancel,
  setActive,
  placeholder,
  disabled,
  cancelLabel,
}: {
  item: ColoredLabel
  active: boolean
  onEdit: (value: ColoredLabel) => void
  onRemove: () => void
  onCancel: () => void
  setActive: (active: boolean) => void
  placeholder?: string
  disabled?: boolean
  cancelLabel?: string
}) => {
  const { document } = useDocument()

  const [inputValue, setInputValue] = useState(item.name)
  const [colorValue, setColorValue] = useState(item.colorHex)

  if (!active || disabled) {
    return (
      <div className="group flex space-s-2 items-center h-7">
        <ColorCircle size="xs" color={colorValue} className="shrink-0" />
        <div className="grow truncate">{item.name}</div>
        {!disabled && (
          <>
            <IconButton
              className="bg-surface opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              variant="tertiaryNeutral"
              icon={<SvgIcon name="edit" />}
              size="sm"
              rounded
              onClick={() => {
                setActive(true)
                setInputValue(item.name)
                setColorValue(item.colorHex)
              }}
            />
            <IconButton
              className="bg-surface opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              variant="tertiaryNeutral"
              icon={<SvgIcon name="close" />}
              size="sm"
              rounded
              onClick={onRemove}
            />
          </>
        )}
      </div>
    )
  }

  const onSave = () => {
    if (!inputValue) {
      return
    }

    onEdit({
      name: inputValue,
      colorHex: colorValue,
      id: item.id,
    })
  }

  const handleCancel = () => {
    setInputValue(item.name)
    setColorValue(item.colorHex)
    onCancel()
  }

  const onKeyDown = event => {
    if (event.key === 'Enter' && inputValue && !disabled) {
      onSave()
    }
    if (event.key === 'Escape') {
      handleCancel()
    }
  }

  const variants = colorStylesFactory(document)

  return (
    <div className="group flex space-s-2 items-center">
      <ColorPicker
        size="sm"
        color={colorValue}
        onChange={value => setColorValue(value)}
        variants={variants}
      />
      <Input
        value={inputValue}
        onChange={e => setInputValue(e?.target?.value)}
        className="min-w-0 grow"
        placeholder={placeholder}
        size="compact"
        autoFocus
        onKeyDown={onKeyDown}
      />
      {!!cancelLabel && (
        <Button
          variant="secondaryNeutral"
          size="lg"
          onClick={handleCancel}
          className="shrink-0"
        >
          {cancelLabel}
        </Button>
      )}
      <IconButton
        variant="primary"
        icon={<SvgIcon name="plus" />}
        size="md"
        rounded
        onClick={onSave}
        disabled={!inputValue}
      />
    </div>
  )
}
